import { Grid, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { differenceInMilliseconds } from 'date-fns/esm';
import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { SnapshotAlarmSetting } from '../../types';
import { getAlarmSettings } from '../alarms/api/getAlarmSettings';
import { analyzerChanged } from '../analyzers/analyzerSlice';
import SingleTraceViewDialog from '../searchWidget/SingleTraceViewDialog';
import {
  selectAlarms,
  subscribeToAnalyzers,
} from '../spectrasWebSocketServer/spectrasWebSocketServerSlice';
import { SocketAlarm } from '../spectrasWebSocketServer/types';
import AlarmLiveTraceDisplay from './AlarmLiveTraceDisplay';
import { LiveAlarmToDisplay } from './types';

const AlarmWallLive = () => {
  const user = useAppSelector((state) => state.auth.user);
  const dispatch = useAppDispatch();
  const alarms: SocketAlarm[] = useAppSelector(selectAlarms);
  const [singleTraceViewOpen, setSingleTraceViewOpen] = useState(false);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState(-1);
  const [selectedAnalyzerId, setSelectedAnalyzerId] = useState(-1);
  const [alarmSettings, setAlarmSettings] = useState<
    SnapshotAlarmSetting | undefined
  >();
  const waitTimeInMilliseconds =
    (alarmSettings?.ignore_interval || 0) *
      (alarmSettings?.ignore_interval_unit === 0
        ? 1
        : alarmSettings?.ignore_interval_unit === 1
        ? 1000
        : 60000) +
    30000;
  useEffect(() => {
    const fetchSettings = async () => {
      const settings = await getAlarmSettings();
      setAlarmSettings(settings);
    };
    fetchSettings();
  }, []);
  const alarmsToDisplay: LiveAlarmToDisplay[] = useMemo(() => {
    const alarmsToDisplay: LiveAlarmToDisplay[] = [];
    const includedSnapshots: number[] = [];
    for (const alarm of alarms) {
      for (const logItem of alarm.alarm_log_items) {
        if (
          Math.abs(
            differenceInMilliseconds(new Date(), new Date(alarm.timestamp))
          ) < waitTimeInMilliseconds &&
          !includedSnapshots.includes(logItem.snapshot_id)
        ) {
          alarmsToDisplay.push({
            alarmName: alarm.name,
            analyzerId: logItem.analyzer_id,
            snapshotId: logItem.snapshot_id,
            alarmLogId: alarm.alarm_log_id,
          });
          includedSnapshots.push(logItem.snapshot_id);
        }
      }
    }
    return alarmsToDisplay.reverse();
  }, [alarms, waitTimeInMilliseconds]);

  useEffect(() => {
    if (!user?.id) return;
    const analyzerIds = alarmsToDisplay.map((alarm) => alarm.analyzerId);
    dispatch(
      subscribeToAnalyzers({
        user_id: user.id,
        analyzer_ids: analyzerIds,
      })
    );
  }, [dispatch, user?.id, alarmsToDisplay]);

  const onWindowClick = (analyzerId: number, snapshotId: number) => {
    setSelectedSnapshotId(snapshotId);
    setSelectedAnalyzerId(analyzerId);
    dispatch(analyzerChanged(analyzerId));
    setSingleTraceViewOpen(true);
  };

  if (!alarmsToDisplay || alarmsToDisplay.length === 0)
    return (
      <Grid
        container
        sx={{ height: '80vh' }}
        justifyContent='center'
        alignItems='center'
      >
        <Typography color={grey[500]}>
          No live alarms from the last 30 seconds to display.
        </Typography>
      </Grid>
    );

  return (
    <Grid container>
      {!singleTraceViewOpen &&
        alarmsToDisplay.map((alarmToDisplay, idx) => (
          <AlarmLiveTraceDisplay
            alarmToDisplay={alarmToDisplay}
            onWindowClick={onWindowClick}
            key={idx}
          />
        ))}
      <SingleTraceViewDialog
        snapshotId={selectedSnapshotId}
        setSnapshotId={setSelectedSnapshotId}
        analyzerId={selectedAnalyzerId}
        open={singleTraceViewOpen}
        handleClose={() => setSingleTraceViewOpen(false)}
        traceMode={0}
      />
    </Grid>
  );
};

export default AlarmWallLive;
